import React, { useState, useEffect, useCallback } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
} from '@chakra-ui/react'
import { AuditLogEntry, AuditLogFilters } from '@/types'
import AuditLogContent from '@/components/Profile/AuditLogContent'
import apiService from '@/services/api'
import { useAppTranslation } from '@/contexts/LanguageContext'

interface UserAuditLogModalProps {
  isOpen: boolean
  onClose: () => void
  userId: number
  userName: string
}

function getDefaultFilters(): AuditLogFilters {
  const today = new Date()
  const sevenDaysAgo = new Date(today)
  sevenDaysAgo.setDate(today.getDate() - 7)
  return {
    date_from: sevenDaysAgo.toISOString().split('T')[0],
    date_to: today.toISOString().split('T')[0],
  }
}

export default function UserAuditLogModal({
  isOpen,
  onClose,
  userId,
  userName,
}: UserAuditLogModalProps) {
  const { t } = useAppTranslation('common')
  const [filters, setFilters] = useState<AuditLogFilters>(getDefaultFilters())
  const [entries, setEntries] = useState<AuditLogEntry[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1, total: 0 })

  useEffect(() => {
    if (!isOpen) {
      setFilters(getDefaultFilters())
      setPage(1)
      setEntries([])
      return
    }
    let cancelled = false
    setIsLoading(true)
    apiService.getUserAuditLog(userId, filters, page)
      .then(result => {
        if (cancelled) return
        setEntries(prev => page === 1 ? result.data : [...prev, ...result.data])
        setPagination(result.meta)
      })
      .catch(() => { if (!cancelled) setEntries([]) })
      .finally(() => { if (!cancelled) setIsLoading(false) })
    return () => { cancelled = true }
  }, [isOpen, userId, filters, page])

  const handleFilterChange = useCallback((newFilters: AuditLogFilters) => {
    setFilters(newFilters)
    setPage(1)
  }, [])

  const handleLoadMore = useCallback(() => {
    setPage(p => p + 1)
  }, [])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t('profile.audit.userAuditLog', { name: userName })}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <AuditLogContent
            entries={entries}
            isLoading={isLoading}
            filters={filters}
            onFilterChange={handleFilterChange}
            pagination={pagination}
            onLoadMore={handleLoadMore}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
